import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import { Badge, Currency, eFilterType, FilteredDataGrid, Grid } from 'ui-toolkit';
import { IEscrowActualIncomingAchData } from '../../types/accounting/data/IEscrowActualIncomingAchData';
import { IEscrowActualIncomingAchQuery } from '../../types/accounting/query/IEscrowActualIncomingAchQuery';
import { ITableChangeState } from 'react-bootstrap-table-next';
import { IncomingTransactionsNavigation } from './IncomingTransactionsNavigation';
import { IsMatchedFilter } from './IsMatchedFilter';
import { cleanDataGridNames, decleanDataGridNames } from './cleanDataGridNames';
import { eMatchedQueryValue } from '../../types/accounting/eMatchedQueryValue';
import { IEscrowActualIncomingAchTableQuery } from '../../types/accounting/query/IEscrowActualIncomingAchTableQuery';
import { IPageData } from 'types/rest/IPageData';
import { useGetUsersByQueryQuery } from 'API/UserApi';
import { IGetUser } from 'types/users/IUser';
import { AmountCents, BankAccountRow } from './SharedColumnDefinitions';
import { calculateRemainingSplitValue } from 'utilities/accounting/calculateRemainingSplitValue';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';

export interface IActualIncomingAchsDataGridProps {
  data?: IPageData<IEscrowActualIncomingAchData>;
  detail?: IEscrowActualIncomingAchData;
  isQueryFetching: boolean;
  query: IEscrowActualIncomingAchTableQuery;
  setSelectedId: (detail: string) => void;
  setQuery: (query: IEscrowActualIncomingAchQuery) => void;
}

const StatusColumn = (column: any, row: IEscrowActualIncomingAchData) => {
  const fullyMatched =
    calculateRemainingSplitValue(
      (row && row.amountCents) || 0,
      (column || []) as IEscrowExpectedIncomingAchData[]
    ) === 0;
  const partiallyMatched = row && row.matches && row.matches.length > 0 && !fullyMatched;
  return (
    <>
      {fullyMatched && <Badge intent={'primary'}>matched</Badge>}
      {partiallyMatched && <Badge intent={'secondary'}>partial</Badge>}
      {!fullyMatched && !partiallyMatched && <Badge intent={'danger'}>unmatched</Badge>}
    </>
  );
};

export const ActualIncomingAchsSearchTable: React.FC<IActualIncomingAchsDataGridProps> = ({
  data,
  detail,
  isQueryFetching,
  query,
  setSelectedId,
  setQuery,
}) => {
  const isMatchedFilterItems: eMatchedQueryValue[] = [
    eMatchedQueryValue.UNMATCHED,
    eMatchedQueryValue.MATCHED,
    eMatchedQueryValue.ALL,
  ];

  const usersQuery = useMemo(
    () =>
      data?.content
        .flatMap((ach) => ach.matches || [])
        .map((ach) => ({ userType: 'ADMIN', userId: ach.matchedBy }) as IGetUser) || [],
    [data]
  );
  const { data: users, isFetching: usersIsFetching } = useGetUsersByQueryQuery(usersQuery);

  const isFetching = isQueryFetching || usersIsFetching;

  return (
    <>
      <Grid templateColumns="1fr max-content" alignItems="center">
        <div>
          <IncomingTransactionsNavigation transactionType="achs" />
        </div>
        <IsMatchedFilter
          matched={query.matched}
          onChange={(matched) => setQuery({ ...query, matched, pageNumber: 1 })}
          items={isMatchedFilterItems}
        />
      </Grid>
      {data && (
        <FilteredDataGrid
          columns={[
            {
              dataField: 'createdAt',
              filterDefault: query.createdAt,
              filterElement: eFilterType.DateRange,
              filterPlaceholder: 'Completed At',
              formatter: (column: any) => DateTime.fromISO(column as string).toFormat('D'),
              headerStyle: { width: '120px' },
              text: 'Completed',
              sort: true,
            },
            {
              // Don't be confused.  Even though the database has separate columns for memo and wells_fargo_transaction_number,
              // we present the WF number as 'memo'.  Though this won't make sense to a developer, it makes sense to the user.
              dataField: 'wellsFargoTransactionNumber',
              filterDefault: query.wellsFargoTransactionNumber,
              filterPlaceholder: 'Memo',
              filterElement: eFilterType.Text,
              headerStyle: { width: 'auto' },
              text: 'Memo',
              sort: true,
            },
            {
              dataField: 'source',
              filterDefault: query.source,
              filterElement: eFilterType.Text,
              filterPlaceholder: 'Sender',
              headerStyle: { width: '160px' },
              text: 'Sender',
              sort: true,
            },
            BankAccountRow,
            AmountCents(query.amountCents),
            {
              dataField: 'unmatchedAmountCents',
              filterElement: eFilterType.Currency,
              filterPlaceholder: 'Unmatched',
              formatter: (_, row) => {
                const { amountCents, matches } = row;
                const remainingAmount =
                  amountCents - (matches || []).reduce((sum, { amountCents }) => sum + amountCents, 0);
                return <Currency value={remainingAmount} valueIsCents={true} />;
              },
              headerStyle: { width: '120px' },
              text: 'Unmatched',
            },
            {
              dataField: 'fileNumber',
              headerStyle: { width: '120px' },
              text: 'File Number',
              sort: true,
            },
            {
              dataField: 'matches',
              formatter: StatusColumn,
              headerStyle: { width: '110px' },
              text: 'Status',
              sort: false,
            },
            {
              dataField: 'matches.name',
              formatter: (column, row) => {
                if (row?.matches?.length) {
                  const userId = row.matches[0].matchedBy;
                  const user = users?.find((user) => user.id === userId);
                  return user?.name || '';
                }

                return '';
              },
              headerStyle: { width: '160px' },
              text: 'Matched By',
              sort: false,
            },
          ]}
          data={data.content}
          keyField="id"
          loading={isFetching}
          key={Number(isFetching)}
          onTableChange={(type: string, tableState: ITableChangeState<IEscrowActualIncomingAchData>) => {
            const newFilters = Object.keys(tableState.filters).reduce(
              (p, key) => ({ ...p, [cleanDataGridNames(key)]: tableState.filters[key].filterVal }),
              {}
            );
            setQuery({
              ...query,
              ...newFilters,
              pageNumber: tableState.page,
              pageSize: tableState.sizePerPage,
              sortField: tableState.sortField || undefined,
              sortOrder: tableState.sortOrder || undefined,
            });
          }}
          remote={true}
          selectRow={{
            mode: 'radio',
            hideSelectColumn: true,
            onSelect: (row) => setSelectedId(row.id),
            clickToSelect: true,
          }}
          pagination={{
            page: query.pageNumber || 1,
            sizePerPage: query.pageSize || 10,
            totalSize: data.totalElements,
          }}
          sort={{ dataField: decleanDataGridNames(query.sortField) || 'UNSET', order: query.sortOrder || 'asc' }}
        />
      )}
    </>
  );
};
